import React from 'react';
import { BookOpen, MapPin, Clock, Share2 } from 'lucide-react';
import type { CaseData } from '@/types/api';
import { formatCaseSummary, formatCaseTitle, getCaseServiceType } from '@/utils/musicDisplay';
import { useOptionalI18n } from '@/i18n';
import { localizeStringList, localizeText } from '@/i18n/options';

interface CaseListCardProps {
  caseData: CaseData;
  onViewDetails?: (caseId: string) => void;
  onShare?: (caseId: string) => void;
  headerBadge?: React.ReactNode;
  headerActions?: React.ReactNode;
  footerActions?: React.ReactNode;
  footerHint?: React.ReactNode;
  hideShareButton?: boolean;
}

export const CaseListCard: React.FC<CaseListCardProps> = ({
  caseData,
  onViewDetails,
  onShare,
  headerBadge,
  headerActions,
  footerActions,
  footerHint = 'View Details →',
  hideShareButton = false,
}) => {
  const title = formatCaseTitle(caseData);
  const { locale } = useOptionalI18n();
  const isZh = locale === 'zh-CN';
  const summary = formatCaseSummary(caseData);
  const serviceType = getCaseServiceType(caseData);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 overflow-hidden cursor-pointer select-none"
      onClick={() => onViewDetails?.(caseData.id)}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <span className="inline-block text-xs font-semibold text-primary bg-primary/10 px-2.5 py-0.5 rounded-full mb-2">
              {serviceType === 'music' ? 'Music' : summary}
            </span>
            <h3 className="text-xl font-bold text-gray-900 truncate">{serviceType === 'music' ? title : caseData.subject}</h3>
          </div>
          {headerActions ? (
            <div
              onClick={(e) => e.stopPropagation()}
              className="shrink-0 mt-1"
            >
              {headerActions}
            </div>
          ) : headerBadge ? (
            <div className="shrink-0 mt-1">{headerBadge}</div>
          ) : !hideShareButton ? (
            <button
              onClick={(e) => { e.stopPropagation(); onShare?.(caseData.id); }}
              className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded-lg transition-colors shrink-0 mt-1"
              title={isZh ? '分享个案' : 'Share case'}
            >
              <Share2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Info */}
      <div className="px-5 py-4 space-y-2.5">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{caseData.areaNote ? `${caseData.areaNote}, ` : ''}{localizeText(locale, caseData.state)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{isZh ? `每周 ${caseData.lessonsPerWeek} 次 · 每次 ${caseData.lessonDuration} 小时` : `${caseData.lessonsPerWeek}× per week · ${caseData.lessonDuration}h each`}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <BookOpen className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{localizeStringList(locale, caseData.teachingLanguages).join(' · ')}</span>
        </div>
        <div>
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
            {caseData.tuitionType === 'online' ? (isZh ? '💻 线上' : '💻 Online') : (isZh ? '📍 线下' : '📍 Physical')}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <div>
          <span className="text-lg font-bold text-primary">RM {caseData.hourlyRate ?? 'TBD'}</span>
          <span className="text-xs text-gray-400 ml-1">/hr</span>
        </div>
        {footerActions ? (
          <div
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2"
          >
            {footerActions}
          </div>
        ) : (
          <span className="text-xs text-gray-400 font-medium">{footerHint === 'View Details →' && isZh ? '查看详情 →' : footerHint}</span>
        )}
      </div>
    </div>
  );
};
